import { useState } from 'react'
import { AircraftChangeInfo, ChangeType } from '../../types/database'
import { AircraftAcknowledgmentsService } from '../../services/aircraft-acknowledgments.service'
import { formatPrice, formatDate } from '../../utils/formatters'
import { shouldShowAcknowledgmentButton } from '../../utils/change-detection'

interface AircraftChangeIndicatorProps {
  changeInfo: AircraftChangeInfo
  onAcknowledged?: () => void
  className?: string
}

export default function AircraftChangeIndicator({ 
  changeInfo, 
  onAcknowledged, 
  className = '' 
}: AircraftChangeIndicatorProps) {
  const [isAcknowledging, setIsAcknowledging] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleAcknowledge = async (changeType: ChangeType) => {
    setIsAcknowledging(true)
    setError(null)

    try {
      let previousValue = null
      let newValue = null

      if (changeType === 'price_change' && changeInfo.price_change_details) {
        previousValue = {
          asking: changeInfo.price_change_details.previous_asking,
          take_price: changeInfo.price_change_details.previous_take
        }
        newValue = {
          asking: changeInfo.price_change_details.new_asking,
          take_price: changeInfo.price_change_details.new_take
        }
      } else if (changeType === 'new_to_market') {
        newValue = {
          date_listed: changeInfo.new_listing_date
        }
      }

      await AircraftAcknowledgmentsService.create({
        aircraft_id: changeInfo.aircraft_id,
        change_type: changeType,
        previous_value: previousValue,
        new_value: newValue
      })

      onAcknowledged?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge change')
    } finally {
      setIsAcknowledging(false)
    }
  }

  const handleAcknowledgeAll = async () => {
    setIsAcknowledging(true)
    setError(null)

    try {
      const promises = []

      if (changeInfo.has_price_change) {
        promises.push(handleAcknowledge('price_change'))
      }

      if (changeInfo.is_new_to_market) {
        promises.push(handleAcknowledge('new_to_market'))
      }

      await Promise.all(promises)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to acknowledge changes')
    } finally {
      setIsAcknowledging(false)
    }
  }

  // Don't render if no changes to show or acknowledge
  if (!shouldShowAcknowledgmentButton(changeInfo)) {
    return null
  }

  const hasMultipleChanges = changeInfo.has_price_change && changeInfo.is_new_to_market

  return (
    <div className={`bg-white border border-gray-200 rounded-lg p-4 ${className}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <h3 className="text-lg font-medium text-gray-900 mb-3">Market Changes Detected</h3>
          
          <div className="space-y-3">
            {/* Price Change Details */}
            {changeInfo.has_price_change && (
              <div className="flex items-start space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full mt-1 flex-shrink-0"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Price Updated</p>
                  {changeInfo.price_change_details && (
                    <div className="text-sm text-gray-600 mt-1">
                      {changeInfo.price_change_details.previous_asking && changeInfo.price_change_details.new_asking && (
                        <div>
                          Asking: {formatPrice(changeInfo.price_change_details.previous_asking.toString())} → {formatPrice(changeInfo.price_change_details.new_asking.toString())}
                        </div>
                      )}
                      {changeInfo.price_change_details.previous_take && changeInfo.price_change_details.new_take && (
                        <div>
                          Take: {formatPrice(changeInfo.price_change_details.previous_take.toString())} → {formatPrice(changeInfo.price_change_details.new_take.toString())}
                        </div>
                      )}
                    </div>
                  )}
                  {!hasMultipleChanges && (
                    <button
                      onClick={() => handleAcknowledge('price_change')}
                      disabled={isAcknowledging}
                      className="mt-2 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
                    >
                      {isAcknowledging ? 'Acknowledging...' : 'Acknowledge Price Change'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* New Listing Details */}
            {changeInfo.is_new_to_market && (
              <div className="flex items-start space-x-3">
                <div className="w-3 h-3 bg-purple-500 rounded-full mt-1 flex-shrink-0"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Newly Listed</p>
                  {changeInfo.new_listing_date && (
                    <p className="text-sm text-gray-600 mt-1">
                      Listed on {formatDate(changeInfo.new_listing_date)}
                    </p>
                  )}
                  {!hasMultipleChanges && (
                    <button
                      onClick={() => handleAcknowledge('new_to_market')}
                      disabled={isAcknowledging}
                      className="mt-2 text-sm text-purple-600 hover:text-purple-800 disabled:opacity-50"
                    >
                      {isAcknowledging ? 'Acknowledging...' : 'Acknowledge New Listing'}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Under Contract Notice (no acknowledgment needed) */}
            {changeInfo.is_under_contract && (
              <div className="flex items-start space-x-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full mt-1 flex-shrink-0"></div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">Under Contract</p>
                  <p className="text-sm text-gray-600 mt-1">
                    This aircraft is currently under contract
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-3 text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}
        </div>

        {/* Acknowledge All Button (for multiple changes) */}
        {hasMultipleChanges && (
          <div className="ml-4 flex-shrink-0">
            <button
              onClick={handleAcknowledgeAll}
              disabled={isAcknowledging}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {isAcknowledging ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Acknowledging...
                </>
              ) : (
                'Acknowledge All Changes'
              )}
            </button>
          </div>
        )}
      </div>

      {/* Last Acknowledged Info */}
      {(changeInfo.last_acknowledged?.price_change || changeInfo.last_acknowledged?.new_to_market) && (
        <div className="mt-4 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Last acknowledged:{' '}
            {changeInfo.last_acknowledged.price_change && (
              <span>Price changes on {formatDate(changeInfo.last_acknowledged.price_change)}</span>
            )}
            {changeInfo.last_acknowledged.price_change && changeInfo.last_acknowledged.new_to_market && ', '}
            {changeInfo.last_acknowledged.new_to_market && (
              <span>New listing on {formatDate(changeInfo.last_acknowledged.new_to_market)}</span>
            )}
          </p>
        </div>
      )}
    </div>
  )
}
